import type { LensCard, LensCatalogueDataset, LensDataset, LensPlanAnchor } from '~/types/leLens';

/**
 * Every dataset on a Lens that reads a governed catalogue table, in spec
 * order. A dataset only counts once its `source.catalogueId` is set: a plain
 * SQL dataset that happens to read the same table by hand is not governed,
 * since the server never compiled its query from the catalogue's own fields.
 */
export function governedDatasets(lens: {
  spec: { cards: ReadonlyArray<LensCard>; datasets?: ReadonlyArray<LensDataset> };
}): Array<LensDataset> {
  return (lens.spec.datasets ?? []).filter(
    (dataset) => dataset.source?.kind === 'governed' && Boolean(dataset.source.catalogueId),
  );
}

/**
 * The governed dataset a Lens anchors on: whichever governed dataset the most
 * cards already read, so the chip and the composer follow what the board is
 * actually showing rather than the order datasets happen to be declared in.
 * A tie, or no cards bound to any governed dataset yet, falls back to the
 * first governed dataset on the spec. Undefined only when the Lens has no
 * governed dataset at all.
 */
export function lensAnchor(lens: {
  spec: { cards: ReadonlyArray<LensCard>; datasets?: ReadonlyArray<LensDataset> };
}): LensPlanAnchor | undefined {
  const governed = governedDatasets(lens);
  if (!governed.length) {
    return undefined;
  }
  const cardCountByDatasetId = new Map<string, number>();
  for (const card of lens.spec.cards) {
    if (!card.datasetId) {
      continue;
    }
    cardCountByDatasetId.set(card.datasetId, (cardCountByDatasetId.get(card.datasetId) ?? 0) + 1);
  }
  let winner = governed[0];
  let winnerCount = cardCountByDatasetId.get(winner.id) ?? 0;
  for (const dataset of governed.slice(1)) {
    const count = cardCountByDatasetId.get(dataset.id) ?? 0;
    if (count > winnerCount) {
      winner = dataset;
      winnerCount = count;
    }
  }
  // Every entry in `governed` carries a catalogueId by construction (the filter above), so this
  // is never actually undefined; the fallback only keeps the return type honest.
  return { datasetId: winner.id, catalogueId: winner.source?.catalogueId ?? '' };
}

/** A dataset id is only ever compared within one Lens spec, so it only has to
 *  read cleanly, not be globally unique. A catalogue id is already such a
 *  slug in the ordinary case (e.g. "sales"); this only reslugifies the rare
 *  id that is not one, from the dataset's own name. */
const SLUG_ID = /^[a-z][a-z0-9_-]*$/;

/**
 * The local dataset id a new Lens's first, anchor dataset is saved under.
 * Prefers the catalogue's own id (readable and stable, e.g. "sales"); falls
 * back to slugifying the dataset's name for a catalogue id that is not
 * already a clean slug.
 */
export function catalogueLocalDatasetId(dataset: Pick<LensCatalogueDataset, 'id' | 'name'>): string {
  if (SLUG_ID.test(dataset.id)) {
    return dataset.id;
  }
  const slug = dataset.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'dataset';
}
